import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiChevronDown, 
  FiChevronRight, 
  FiPackage, 
  FiTrendingUp 
} = FiIcons;

const CustomerSalesHistory = ({ customerId }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set(['abiraterone-acetate']));

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Mock historical sales data
  const salesHistoryGroups = [
    {
      id: 'abiraterone-acetate',
      name: 'Abiraterone Acetate Tablets',
      products: [
        {
          strength: '250mg',
          packSize: '30 count',
          ndc: '72578-001-30',
          salesUSD2022: 3200000,
          salesUSD2023: 4100000,
          salesUSD2024: 5200000,
          volumePacks2022: 22000,
          volumePacks2023: 28200,
          volumePacks2024: 35700,
          volumeEaches2022: 660000,
          volumeEaches2023: 846000,
          volumeEaches2024: 1071000
        },
        {
          strength: '500mg',
          packSize: '60 count',
          ndc: '72578-001-60',
          salesUSD2022: 2100000,
          salesUSD2023: 2800000,
          salesUSD2024: 3300000,
          volumePacks2022: 7350,
          volumePacks2023: 9800,
          volumePacks2024: 11550,
          volumeEaches2022: 441000,
          volumeEaches2023: 588000,
          volumeEaches2024: 693000
        }
      ]
    },
    {
      id: 'atorvastatin',
      name: 'Atorvastatin Tablets',
      products: [
        {
          strength: '10mg',
          packSize: '90 count',
          ndc: '72578-002-90',
          salesUSD2022: 1800000,
          salesUSD2023: 1950000,
          salesUSD2024: 2100000,
          volumePacks2022: 62100,
          volumePacks2023: 67400,
          volumePacks2024: 72600,
          volumeEaches2022: 5589000,
          volumeEaches2023: 6066000,
          volumeEaches2024: 6534000
        },
        {
          strength: '20mg',
          packSize: '90 count',
          ndc: '72578-002-91',
          salesUSD2022: 1750000,
          salesUSD2023: 1900000,
          salesUSD2024: 2050000,
          volumePacks2022: 53800,
          volumePacks2023: 58500,
          volumePacks2024: 63100,
          volumeEaches2022: 4842000,
          volumeEaches2023: 5265000,
          volumeEaches2024: 5679000
        }
      ]
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="space-y-6">
      {/* Sales History Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiTrendingUp} className="text-blue-600" />
            3-Year Sales History Analysis
          </h3>
          <p className="text-gray-600 mt-1">Historical performance by product and strength</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Product Group</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Strength & Pack</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">NDC</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900" colSpan="3">Sales (USD)</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900" colSpan="3">Volume (Packs)</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900" colSpan="3">Volume (Eaches/NSP Ext Units)</th>
              </tr>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th></th>
                <th></th>
                <th></th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2022</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2023</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2024</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2022</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2023</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2024</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2022</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2023</th>
                <th className="text-center py-2 px-4 text-sm text-gray-600">2024</th>
              </tr>
            </thead>
            <tbody>
              {salesHistoryGroups.map((group) => (
                <React.Fragment key={group.id}>
                  {/* Group Header Row */}
                  <tr className="bg-blue-50 border-b border-gray-200 cursor-pointer hover:bg-blue-100 transition-colors">
                    <td 
                      colSpan="12" 
                      className="py-4 px-6"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon 
                          icon={expandedGroups.has(group.id) ? FiChevronDown : FiChevronRight}
                          className="text-blue-600"
                        />
                        <SafeIcon icon={FiPackage} className="text-blue-600" />
                        <span className="font-semibold text-blue-900">{group.name}</span>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Product Detail Rows */}
                  {expandedGroups.has(group.id) && group.products.map((product, index) => (
                    <tr 
                      key={`${group.id}-${index}`}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 pl-12">
                        <span className="text-sm text-gray-600">→</span>
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-medium text-gray-900">{product.strength}</p>
                          <p className="text-sm text-gray-600">{product.packSize}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {product.ndc}
                        </span>
                      </td>
                      {/* Sales USD columns */}
                      <td className="py-4 px-6 text-center text-sm">{formatCurrency(product.salesUSD2022)}</td>
                      <td className="py-4 px-6 text-center text-sm">{formatCurrency(product.salesUSD2023)}</td>
                      <td className="py-4 px-6 text-center text-sm font-medium">{formatCurrency(product.salesUSD2024)}</td>
                      {/* Volume Packs columns */}
                      <td className="py-4 px-6 text-center text-sm">{formatNumber(product.volumePacks2022)}</td>
                      <td className="py-4 px-6 text-center text-sm">{formatNumber(product.volumePacks2023)}</td>
                      <td className="py-4 px-6 text-center text-sm font-medium">{formatNumber(product.volumePacks2024)}</td>
                      {/* Volume Eaches columns */}
                      <td className="py-4 px-6 text-center text-sm">{formatNumber(product.volumeEaches2022)}</td>
                      <td className="py-4 px-6 text-center text-sm">{formatNumber(product.volumeEaches2023)}</td>
                      <td className="py-4 px-6 text-center text-sm font-medium">{formatNumber(product.volumeEaches2024)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">+27.3%</p>
              <p className="text-sm text-gray-600">3-Year CAGR</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Consistent growth across product portfolio</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiPackage} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">12.65M</p>
              <p className="text-sm text-gray-600">Total Volume (2024)</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Units across all product lines</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">$12.65M</p>
              <p className="text-sm text-gray-600">Total Sales (2024)</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Revenue across all products</p>
        </div>
      </motion.div>
    </div>
  );
};

export default CustomerSalesHistory;